/**
 * Broker `localId → serverId` — Phase 3.3.7.
 *
 * Coordonne le remapping des ids locaux (`ev_local_1`,
 * `tk_local_2`, UUID v4 client pour les binaires, etc.) vers
 * leurs équivalents UUID serveur après replay d'une mutation
 * offline-first. Sans ce remapping, le store domain garde son
 * `localId` et le prochain GET refresh fait apparaître l'item
 * serveur **en plus** du local → doublon visuel transitoire.
 *
 * Architecture :
 *
 *   1. `mutationsReplay.ts` capture la réponse serveur après un
 *      POST réussi (replay ou inline). Si la mutation portait un
 *      `localId`, on extrait l'`id` serveur du body et on dispatch
 *      un `CustomEvent` `nestor:mutation-remap`.
 *   2. Le broker écoute cet event au boot (via
 *      `installLocalIdRemapBroker`) et :
 *      a. Parse le `path` pour déterminer la sub-entité
 *         (`events`, `tasks`, …).
 *      b. Appelle l'`apply*SyncResult(homeId, {idMap})`
 *         correspondant dans `stores/homes.ts`, avec un map
 *         `localId → serverId` à 1 entrée.
 *
 * **Découpé de mutationsReplay** pour éviter un cycle d'import :
 * mutationsReplay émet ; le broker (qui dépend de `stores/homes.ts`)
 * consomme.
 *
 * **Path → entité dispatch** : regex simple. Chaque route serveur a
 * un pattern unique :
 *
 *   - `/homes/<uuid>/events` → events
 *   - `/homes/<uuid>/tasks` → tasks
 *   - `/homes/<uuid>/messages` → messages
 *   - `/homes/<uuid>/photos` → photos
 *   - `/homes/<uuid>/journal` → journal
 *   - `/homes/<uuid>/liaison` → liaison
 *   - `/homes/<uuid>/expenses` → expenses
 *   - `/homes/<uuid>/bag` → bag
 *   - `/homes/<uuid>/school` → school
 *
 * Les PATCH/DELETE (`/<entity>/<id>`) n'ont pas de `localId` en
 * 3.3.7 — leur `id` cible est déjà dans le path, pas de mapping
 * à faire (le store local connaît déjà l'id).
 */
import type { HomeId } from '@nestor/shared-types';
import {
  applyBagSyncResult,
  applyEventsSyncResult,
  applyExpensesSyncResult,
  applyJournalSyncResult,
  applyLiaisonSyncResult,
  applyMessagesSyncResult,
  applyPhotosSyncResult,
  applySchoolSyncResult,
  applyTasksSyncResult,
} from '../stores/homes.js';

/**
 * Détail de l'event `nestor:mutation-remap` émis par
 * `mutationsReplay` après un POST réussi.
 */
export interface LocalIdRemapDetail {
  /** Path API du POST (ex. `/homes/<uuid>/events`). */
  path: string;
  localId: string;
  serverId: string;
}

export const REMAP_EVENT_NAME = 'nestor:mutation-remap';

/**
 * Pattern minimal pour extraire `homeId` + nom de sub-entité du
 * path. On accepte n'importe quel `homeId` (UUID ou local id à la
 * limite — le payload sync gère via `applyXSyncResult` qui filtre
 * sur l'existence du foyer).
 */
const PATH_RE =
  /^\/homes\/([^/]+)\/(events|tasks|messages|photos|journal|liaison|expenses|bag|school)$/;

/**
 * Émet l'event de remap. Helper exposé pour `mutationsReplay`.
 * Best-effort : si `window` n'existe pas (SSR, certains tests), no-op.
 */
export function emitLocalIdRemap(detail: LocalIdRemapDetail): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent<LocalIdRemapDetail>(REMAP_EVENT_NAME, { detail }));
  } catch (err) {
    console.warn('[localIdRemap] dispatch failed:', err);
  }
}

/**
 * Applique un remap unique sur le store domain approprié. Construit
 * un `idMap` à 1 entrée et appelle l'`apply*SyncResult` correspondant.
 *
 * Exposé pour test unitaire (cf. `local-id-remap.test.ts`). En
 * runtime c'est `handleRemapEvent` qui l'appelle.
 */
export function applyLocalIdRemap(detail: LocalIdRemapDetail): boolean {
  const match = PATH_RE.exec(detail.path);
  if (!match) return false;
  const homeId = match[1]! as HomeId;
  const entity = match[2]!;
  const idMap = new Map([[detail.localId, detail.serverId]]);

  switch (entity) {
    case 'events':
      applyEventsSyncResult(homeId, { idMap });
      return true;
    case 'tasks':
      applyTasksSyncResult(homeId, { idMap });
      return true;
    case 'messages':
      applyMessagesSyncResult(homeId, { idMap });
      return true;
    case 'photos':
      applyPhotosSyncResult(homeId, { idMap });
      return true;
    case 'journal':
      applyJournalSyncResult(homeId, { idMap });
      return true;
    case 'liaison':
      applyLiaisonSyncResult(homeId, { idMap });
      return true;
    case 'expenses':
      applyExpensesSyncResult(homeId, { idMap });
      return true;
    case 'bag':
      applyBagSyncResult(homeId, { idMap });
      return true;
    case 'school':
      applySchoolSyncResult(homeId, { idMap });
      return true;
    default:
      return false;
  }
}

function handleRemapEvent(e: Event): void {
  const detail = (e as CustomEvent<LocalIdRemapDetail>).detail;
  if (!detail || typeof detail.localId !== 'string' || typeof detail.serverId !== 'string') {
    return;
  }
  applyLocalIdRemap(detail);
}

/**
 * Câble l'écouteur global. À appeler une fois au boot
 * (`main.ts`). Retourne une fonction de désinstallation pour les
 * tests / HMR.
 */
export function installLocalIdRemapBroker(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener(REMAP_EVENT_NAME, handleRemapEvent);
  return () => {
    window.removeEventListener(REMAP_EVENT_NAME, handleRemapEvent);
  };
}
